import { BOARD_WIDTH, LANE_HEIGHT, ROW_PADDING_Y, toXPx } from '../../../shared/lib/schedule';
import { BOARD_LAYER } from '../model/boardLayers';
import { MIN_WORK_MINUTES } from '../../../shared/lib/schedule';
import type { PreviewLayout } from '../../../shared/lib/schedule';

/**
 * Below this many pixels a strip drops its `45 min` — the number would be truncated to noise, and
 * the strip's width already says roughly the same thing.
 */
const SEGMENT_LABEL_MIN_WIDTH = 34;

/** How far the strips that are *not* being resized recede. */
const INACTIVE_OPACITY = 0.4;

interface Props {
  layout: PreviewLayout;
  lane: number;
  /** The proposed range overlaps something — the drop will very likely be rejected. */
  colliding: boolean;
  /**
   * The proposed range leaves the technician's shift. Amber, not red: the server accepts this once
   * the `Außerhalb der Arbeitszeit` dialog is confirmed, where an overlap is a harder no.
   */
  outsideShift: boolean;
}

/**
 * Where the block will land, split into Anfahrt / Arbeit / Abfahrt. Drawn in the row the pointer is
 * over, which is not necessarily the row the block came from.
 *
 * The strip being resized keeps its full colour and the others recede, so a drag on the left edge
 * reads as "the Anfahrt is changing" rather than as the whole Termin moving.
 */
export const DragGhost = ({ layout, lane, colliding, outsideShift }: Props) => {
  const startPx = toXPx(layout.startMin);
  const endPx = toXPx(layout.endMin);

  // The ghost is cut to the drawn window, but the strips inside it keep their true geometry — so a
  // block hanging off 06:00 stays aligned with itself instead of squashing into view.
  const left = Math.max(0, startPx);
  const right = Math.min(BOARD_WIDTH, Math.max(endPx, startPx + toXPx(MIN_WORK_MINUTES)));
  const trackWidth = Math.max(endPx - startPx, toXPx(MIN_WORK_MINUTES));

  const total = layout.segments.reduce((sum, segment) => sum + segment.minutes, 0);

  return (
    <div
      className={`pointer-events-none absolute overflow-hidden rounded ring-2 ${
        BOARD_LAYER.ghost
      } ${colliding ? 'ring-red-600' : outsideShift ? 'ring-amber-500' : 'ring-white'}`}
      style={{
        left,
        width: Math.max(0, right - left),
        top: ROW_PADDING_Y + lane * LANE_HEIGHT,
        height: LANE_HEIGHT - 6,
      }}
      aria-hidden
    >
      <div
        className="absolute bottom-0 top-0 flex gap-px"
        style={{ left: startPx - left, width: trackWidth }}
      >
        {layout.segments.map((segment) => {
          if (segment.minutes <= 0) return null;
          const widthPx = total > 0 ? (segment.minutes / total) * trackWidth : 0;

          return (
            <div
              key={segment.kind}
              className="flex h-full min-w-0 items-center justify-center overflow-hidden rounded-sm"
              style={{
                flex: segment.minutes,
                backgroundColor: segment.color,
                opacity: segment.active ? 1 : INACTIVE_OPACITY,
                minWidth: 4,
              }}
            >
              {widthPx >= SEGMENT_LABEL_MIN_WIDTH && (
                <span className="truncate px-1 text-[10px] font-semibold tabular-nums text-white">
                  {segment.label}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Over the strips, not instead of them — the split stays readable while red still means
          "this drop will be rejected". Red wins over amber: an overlap is the harder problem, and
          two tinted overlays would only muddy the colour. */}
      {colliding && <div className="absolute inset-0 bg-red-600/45" />}
      {!colliding && outsideShift && <div className="absolute inset-0 bg-amber-500/40" />}
    </div>
  );
};
